import { useEffect, useMemo, useState } from 'react';
import { Download, FileText, Mail, Search } from 'lucide-react';
import { api, formatMoney, formatDateTime, downloadCsv } from '../../lib/api';

const STATUS_STYLES = {
  pending:   'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  failed:    'bg-rose-100 text-rose-700',
  refunded:  'bg-slate-200 text-slate-700',
};

export default function AdminPurchases() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState({}); // { [purchaseId]: 'download' | 'resend' }
  const [notice, setNotice] = useState(null); // { kind: 'ok' | 'error', text }

  useEffect(() => {
    api('/api/admin/purchases')
      .then((d) => setPurchases(d.purchases || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return purchases.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (!term) return true;
      return (
        p.user_email?.toLowerCase().includes(term) ||
        p.order_buyer_email?.toLowerCase().includes(term) ||
        p.order_organization?.toLowerCase().includes(term) ||
        p.course_title?.toLowerCase().includes(term) ||
        p.provider_transaction_id?.toLowerCase().includes(term)
      );
    });
  }, [purchases, statusFilter, search]);

  const exportCsv = () => {
    const rows = filtered.map((p) => ({
      id: p.id,
      created_at: p.created_at,
      completed_at: p.completed_at,
      status: p.status,
      user_email: p.user_email,
      course_title: p.course_title,
      amount: (p.amount_cents / 100).toFixed(2),
      currency: p.currency,
      provider_transaction_id: p.provider_transaction_id,
      payment_method: p.payment_method_summary,
      group_order_id: p.order_id || '',
      group_buyer_email: p.order_buyer_email || '',
      group_organization: p.order_organization || '',
    }));
    downloadCsv(rows, `learnable-purchases-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const hasReceipt = (p) => p.status === 'completed' || p.status === 'refunded';
  // Group seats share the order's receipt, which goes to the buyer.
  const receiptEmail = (p) => (p.order_id ? p.order_buyer_email : p.user_email);
  const setRowBusy = (id, v) => setBusy((b) => ({ ...b, [id]: v }));

  const downloadReceipt = async (p) => {
    setRowBusy(p.id, 'download');
    setNotice(null);
    try {
      const res = await fetch(`/api/admin/purchases/${p.id}/receipt.pdf`, { credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') || '')?.[1]
        || `receipt-${p.id}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setNotice({ kind: 'error', text: `Couldn't download receipt: ${e.message}` });
    } finally {
      setRowBusy(p.id, null);
    }
  };

  const resendReceipt = async (p) => {
    const to = receiptEmail(p);
    const what = p.order_id ? `the group order receipt (${p.order_seat_count} registrations)` : 'the receipt';
    if (!confirm(`Email ${what} for "${p.course_title}" to ${to}?`)) return;
    setRowBusy(p.id, 'resend');
    setNotice(null);
    try {
      const d = await api(`/api/admin/purchases/${p.id}/resend-receipt`, { method: 'POST' });
      setNotice({ kind: 'ok', text: `Receipt sent to ${d.sent_to}.` });
    } catch (e) {
      const msg = e.message === 'email_not_configured' ? 'email is not configured on this server' : e.message;
      setNotice({ kind: 'error', text: `Couldn't resend receipt: ${msg}` });
    } finally {
      setRowBusy(p.id, null);
    }
  };

  const counts = {
    all: purchases.length,
    completed: purchases.filter((p) => p.status === 'completed').length,
    pending: purchases.filter((p) => p.status === 'pending').length,
    failed: purchases.filter((p) => p.status === 'failed').length,
    refunded: purchases.filter((p) => p.status === 'refunded').length,
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">Purchases <span className="text-slate-400 text-base font-normal">({counts.all})</span></h1>
        <button
          onClick={exportCsv}
          className="inline-flex items-center gap-2 text-sm bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-lg font-medium"
          disabled={!filtered.length}
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 text-sm">
          {['all', 'completed', 'pending', 'failed', 'refunded'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-md font-medium capitalize ${
                statusFilter === s ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-200'
              }`}
            >
              {s} ({counts[s]})
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            placeholder="Search email, course, txn ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-72"
          />
        </div>
      </div>

      {notice && (
        <div
          className={`mb-4 px-4 py-2.5 rounded-lg text-sm flex items-center justify-between gap-3 ${
            notice.kind === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'
          }`}
        >
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} className="text-xs font-medium opacity-70 hover:opacity-100">Dismiss</button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No purchases match.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Date</th>
                <th className="text-left px-3 py-3 font-semibold">User</th>
                <th className="text-left px-3 py-3 font-semibold">Course</th>
                <th className="text-right px-3 py-3 font-semibold">Amount</th>
                <th className="text-left px-3 py-3 font-semibold">Status</th>
                <th className="text-left px-3 py-3 font-semibold hidden lg:table-cell">Method</th>
                <th className="text-left px-3 py-3 font-semibold hidden xl:table-cell">Txn ID</th>
                <th className="text-right px-5 py-3 font-semibold">Receipt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-5 py-3 text-slate-500 whitespace-nowrap text-xs">
                    {formatDateTime(p.completed_at || p.created_at)}
                  </td>
                  <td className="px-3 py-3 text-slate-700">
                    {p.user_email}
                    {p.order_id && (
                      <div className="text-xs text-slate-500" title={`Group order ${p.order_id}`}>
                        Group of {p.order_seat_count} · paid by {p.order_organization || p.order_buyer_name}
                        {p.order_buyer_email !== p.user_email?.toLowerCase() && ` (${p.order_buyer_email})`}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-slate-900 font-medium">{p.course_title}</td>
                  <td className="px-3 py-3 text-right font-mono text-slate-700">
                    {formatMoney(p.amount_cents, p.currency)}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${STATUS_STYLES[p.status]}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 hidden lg:table-cell text-slate-500">{p.payment_method_summary || '—'}</td>
                  <td className="px-3 py-3 hidden xl:table-cell text-slate-400 font-mono text-xs">
                    {p.provider_transaction_id || '—'}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {hasReceipt(p) ? (
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => downloadReceipt(p)}
                          disabled={!!busy[p.id]}
                          title="Download receipt (PDF)"
                          aria-label="Download receipt"
                          className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-200 disabled:opacity-40"
                        >
                          <FileText size={15} className={busy[p.id] === 'download' ? 'animate-pulse' : ''} />
                        </button>
                        <button
                          onClick={() => resendReceipt(p)}
                          disabled={!!busy[p.id]}
                          title={`Resend receipt to ${receiptEmail(p)}`}
                          aria-label="Resend receipt"
                          className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-200 disabled:opacity-40"
                        >
                          <Mail size={15} className={busy[p.id] === 'resend' ? 'animate-pulse' : ''} />
                        </button>
                      </div>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
